import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Badge,
  Table,
} from 'reactstrap';
import moment from 'moment';
import RequestLicenseModal, { LicenseRequestData } from './RequestLicenseModal';
import { useOrganizationStore } from '../../store/organization.store';
import { useToast } from '../../contexts/ToastContext';

interface LicenseField {
  label: string;
  value: string;
}

interface License {
  id: string;
  status: string;
  licenseType: string;
  dateExpires: number | null;
  dateStart: number | null;
  createdAt: number;
  jobTitle?: string;
  client?: LicenseField | null;
  agency?: LicenseField | null;
  flightingPlatform?: LicenseField[];
  territories?: LicenseField[];
}

interface LicenseRequest {
  id: string;
  status: string;
  licenseType: string;
  dateExpires: number | null;
  dateStart: number | null;
  createdAt: number;
  jobTitle?: string;
  client?: LicenseField | null;
  agency?: LicenseField | null;
  flightingPlatform?: LicenseField[];
  territories?: LicenseField[];
  rejectedAt?: number;
  rejectionReason?: string;
}

interface ClientLicenseHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  trackName: string;
  trackId: string;
  sourceOrgId: string;
  sourceOrgName?: string;
  licenses: License[];
  isLoading?: boolean;
  firebase: any;
}

const ClientLicenseHistoryModal: React.FC<ClientLicenseHistoryModalProps> = ({
  isOpen,
  onClose,
  trackName,
  trackId,
  sourceOrgId,
  sourceOrgName,
  licenses,
  isLoading = false,
  firebase,
}) => {
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [licenseRequests, setLicenseRequests] = useState<LicenseRequest[]>([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const { currentOrgId, currentOrgName } = useOrganizationStore();
  const { showSuccess, showError } = useToast();

  // Subscribe to license requests for this track
  useEffect(() => {
    if (!isOpen || !sourceOrgId || !trackId || !currentOrgId || !firebase) {
      return;
    }

    setRequestsLoading(true);
    const unsubscribe = firebase.subscribeClientLicenseRequestsForTrack(
      sourceOrgId,
      trackId,
      currentOrgId,
      (requests: LicenseRequest[]) => {
        setLicenseRequests(requests);
        setRequestsLoading(false);
      }
    );

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [isOpen, sourceOrgId, trackId, currentOrgId, firebase]);

  const handleRequestLicense = async (requestData: LicenseRequestData) => {
    if (!firebase || !currentOrgId || !sourceOrgId) return;

    try {
      await firebase.createLicenseRequest(
        sourceOrgId,
        trackId,
        trackName,
        currentOrgId,
        currentOrgName || 'Unknown Organization',
        requestData
      );
      showSuccess('License request submitted successfully!');
      setShowRequestModal(false);
    } catch (error) {
      console.error('Error submitting license request:', error);
      showError('Failed to submit license request');
      throw error;
    }
  };

  const getStatusBadge = (item: License | LicenseRequest, isRequest: boolean = false) => {
    // Handle license requests
    if (isRequest) {
      if (item.status === 'pending') {
        return <Badge color="info">Requested</Badge>;
      }
      if (item.status === 'rejected') {
        return <Badge color="danger">Rejected</Badge>;
      }
      // Approved requests become licenses, so this shouldn't show
      return <Badge color="secondary">Processed</Badge>;
    }

    // Handle licenses
    const isExpired = item.dateExpires && item.dateExpires < Date.now();

    if (item.status === 'inactive' || isExpired) {
      return <Badge className="badge-inactive">Inactive</Badge>;
    }
    if (item.status === 'pending') {
      return <Badge color="warning">Pending</Badge>;
    }
    return <Badge className="badge-accent">Active</Badge>;
  };

  const formatDate = (timestamp: number | null) => {
    if (!timestamp) return 'Blanket';
    return moment(timestamp).format('DD/MM/YYYY');
  };

  // Format array fields with limit and "+X more" for overflow
  const formatArrayField = (items: LicenseField[] | undefined, limit: number = 2) => {
    if (!items || items.length === 0) return { display: '-', tooltip: '' };

    const labels = items.map(item => item.label);

    if (labels.length <= limit) {
      return { display: labels.join(', '), tooltip: '' };
    }

    const displayed = labels.slice(0, limit).join(', ');
    const remaining = labels.length - limit;
    const fullList = labels.join(', ');

    return {
      display: `${displayed}, +${remaining} more`,
      tooltip: fullList,
    };
  };

  const isLicenseActive = (license: License) => {
    if (license.status === 'inactive') return false;
    if (license.dateExpires && license.dateExpires < Date.now()) return false;
    return true;
  };

  // Filter out approved requests (they become licenses)
  const pendingOrRejectedRequests = licenseRequests.filter(
    req => req.status === 'pending' || req.status === 'rejected'
  );

  // Combine licenses and requests into a unified list for display
  type DisplayItem = (License | LicenseRequest) & { isRequest?: boolean };

  const combinedItems: DisplayItem[] = [
    ...licenses.map(l => ({ ...l, isRequest: false })),
    ...pendingOrRejectedRequests.map(r => ({ ...r, isRequest: true })),
  ];

  // Sort: pending requests first, then active licenses, then inactive/rejected by createdAt desc
  const sortedItems = combinedItems.sort((a, b) => {
    // Pending requests come first
    if (a.isRequest && a.status === 'pending' && !(b.isRequest && b.status === 'pending')) return -1;
    if (b.isRequest && b.status === 'pending' && !(a.isRequest && a.status === 'pending')) return 1;

    // Then active licenses
    const aActive = !a.isRequest && isLicenseActive(a as License);
    const bActive = !b.isRequest && isLicenseActive(b as License);
    if (aActive && !bActive) return -1;
    if (!aActive && bActive) return 1;

    // Then by createdAt desc
    return b.createdAt - a.createdAt;
  });

  // For backwards compatibility with prefill logic
  const sortedLicenses = [...licenses].sort((a, b) => {
    const aActive = isLicenseActive(a);
    const bActive = isLicenseActive(b);
    if (aActive && !bActive) return -1;
    if (!aActive && bActive) return 1;
    return b.createdAt - a.createdAt;
  });

  // Get the most recent license for pre-filling request form
  const mostRecentLicense = sortedLicenses.length > 0 ? sortedLicenses[0] : null;
  const prefillData = mostRecentLicense ? {
    licenseType: mostRecentLicense.licenseType,
    jobTitle: mostRecentLicense.jobTitle,
    client: mostRecentLicense.client,
    agency: mostRecentLicense.agency,
    flightingPlatform: mostRecentLicense.flightingPlatform,
    territories: mostRecentLicense.territories,
  } : null;

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      size="lg"
      style={{
        color: '#ffffff',
        maxWidth: '900px',
        margin: '1.75rem auto',
      }}
      contentClassName="delete-confirm-modal"
    >
      <ModalHeader
        toggle={onClose}
        style={{
          backgroundColor: 'var(--chatbot-color-2)',
          color: '#ffffff',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}
        close={
          <button
            className="close"
            onClick={onClose}
            style={{
              color: 'var(--accent-1-color)',
              opacity: 1,
              textShadow: 'none',
              background: 'none',
              border: 'none',
              fontSize: '1.75rem',
              fontWeight: 'normal',
              cursor: 'pointer',
            }}
          >
            &times;
          </button>
        }
      >
        <span style={{ color: '#fff' }}>License History - {trackName}</span>
      </ModalHeader>
      <ModalBody
        style={{
          backgroundColor: 'var(--chatbot-color-1)',
          color: '#ffffff',
          padding: '1.5rem',
          maxHeight: '400px',
          overflowY: 'auto',
        }}
      >
        {sourceOrgName && (
          <p style={{ marginBottom: '1rem', fontSize: '0.9rem' }}>
            <span className="text-muted">Licensed from: </span>
            <span style={{ color: '#fff' }}>{sourceOrgName}</span>
          </p>
        )}

        {isLoading || requestsLoading ? (
          <div className="text-center py-4">
            <small className="text-muted">Loading...</small>
          </div>
        ) : sortedItems.length === 0 ? (
          <div className="text-center py-4">
            <p className="text-muted mb-0">No licenses or requests found for this track.</p>
          </div>
        ) : (
          <Table
            dark
            responsive
            size="sm"
            style={{
              backgroundColor: 'transparent',
              marginBottom: 0,
            }}
          >
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.2)' }}>
                <th style={{ border: 'none', padding: '0.5rem' }}>Status</th>
                <th style={{ border: 'none', padding: '0.5rem' }}>Expires</th>
                <th style={{ border: 'none', padding: '0.5rem' }}>Job Title</th>
                <th style={{ border: 'none', padding: '0.5rem' }}>Brand</th>
                <th style={{ border: 'none', padding: '0.5rem' }}>Platforms</th>
                <th style={{ border: 'none', padding: '0.5rem' }}>Territories</th>
                <th style={{ border: 'none', padding: '0.5rem' }}>Created</th>
              </tr>
            </thead>
            <tbody>
              {sortedItems.map((item) => {
                const isActive = !item.isRequest && isLicenseActive(item as License);
                const isRejected = item.isRequest && item.status === 'rejected';
                const opacity = isActive || (item.isRequest && item.status === 'pending') ? 1 : 0.6;

                return (
                  <tr
                    key={`${item.isRequest ? 'req' : 'lic'}-${item.id}`}
                    style={{
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                      opacity,
                    }}
                  >
                    <td style={{ border: 'none', padding: '0.75rem 0.5rem', verticalAlign: 'middle' }}>
                      {getStatusBadge(item, item.isRequest)}
                      {isRejected && (item as LicenseRequest).rejectionReason && (
                        <div style={{ marginTop: '0.25rem' }}>
                          <small className="text-muted" style={{ fontSize: '0.7rem', fontStyle: 'italic' }}>
                            {(item as LicenseRequest).rejectionReason}
                          </small>
                        </div>
                      )}
                    </td>
                    <td style={{ border: 'none', padding: '0.75rem 0.5rem', verticalAlign: 'middle' }}>
                      <small>{formatDate(item.dateExpires)}</small>
                    </td>
                    <td style={{ border: 'none', padding: '0.75rem 0.5rem', verticalAlign: 'middle' }}>
                      <small className="text-muted">{item.jobTitle || '-'}</small>
                    </td>
                    <td style={{ border: 'none', padding: '0.75rem 0.5rem', verticalAlign: 'middle' }}>
                      <small className="text-muted">{item.client?.label || '-'}</small>
                    </td>
                    <td style={{ border: 'none', padding: '0.75rem 0.5rem', verticalAlign: 'middle' }}>
                      {(() => {
                        const { display, tooltip } = formatArrayField(item.flightingPlatform, 1);
                        return (
                          <small
                            className="text-muted"
                            title={tooltip}
                            style={tooltip ? { cursor: 'help' } : undefined}
                          >
                            {display}
                          </small>
                        );
                      })()}
                    </td>
                    <td style={{ border: 'none', padding: '0.75rem 0.5rem', verticalAlign: 'middle' }}>
                      {(() => {
                        const { display, tooltip } = formatArrayField(item.territories, 1);
                        return (
                          <small
                            className="text-muted"
                            title={tooltip}
                            style={tooltip ? { cursor: 'help' } : undefined}
                          >
                            {display}
                          </small>
                        );
                      })()}
                    </td>
                    <td style={{ border: 'none', padding: '0.75rem 0.5rem', verticalAlign: 'middle' }}>
                      <small className="text-muted">
                        {moment(item.createdAt).format('DD/MM/YYYY')}
                      </small>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </ModalBody>
      <ModalFooter
        style={{
          backgroundColor: 'var(--chatbot-color-2)',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          justifyContent: 'space-between',
        }}
      >
        <Button
          className="btn-primary-custom btn-ripple"
          onClick={() => setShowRequestModal(true)}
        >
          <span style={{ position: 'relative', zIndex: 2 }}>Request License</span>
        </Button>
        <Button
          className="btn-info-custom"
          onClick={onClose}
        >
          <span style={{ position: 'relative', zIndex: 2 }}>Close</span>
        </Button>
      </ModalFooter>

      {/* Request License Modal */}
      <RequestLicenseModal
        isOpen={showRequestModal}
        onClose={() => setShowRequestModal(false)}
        onSubmit={handleRequestLicense}
        trackName={trackName}
        sourceOrgName={sourceOrgName}
        firebase={firebase}
        prefillData={prefillData}
      />
    </Modal>
  );
};

export default ClientLicenseHistoryModal;
